import { createId } from "@paralleldrive/cuid2"
import { getAnalytics } from "firebase/analytics"
import { FirebaseError, initializeApp } from "firebase/app"
import {
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  type NextOrObserver,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User,
  verifyPasswordResetCode,
} from "firebase/auth"
import { getBlob, getDownloadURL, getStorage, listAll, ref, uploadBytes } from "firebase/storage"
import { buildAuthContinueUrl } from "./auth-continue-url"

import { InvalidCredentialError } from "./errors"

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const app = initializeApp(firebaseConfig)
const firebaseAuth = getAuth(app)

firebaseAuth.languageCode = "ja"

export const analytics = getAnalytics(app)

export const auth = {
  currentUser: () => firebaseAuth.currentUser,
  signInWithEmailAndPassword: (email: string, password: string) =>
    signInWithEmailAndPassword(firebaseAuth, email, password).catch((e) => {
      if (e instanceof FirebaseError && e.code === "auth/invalid-credential") {
        throw new InvalidCredentialError()
      }

      throw e
    }),
  createUserWithEmailAndPassword: (email: string, password: string) =>
    createUserWithEmailAndPassword(firebaseAuth, email, password),
  sendEmailVerification: (user: User, continuePath?: string | null) =>
    sendEmailVerification(user, { url: buildAuthContinueUrl(continuePath) }),
  applyActionCode: (code: string) => applyActionCode(firebaseAuth, code),
  sendPasswordResetEmail: (email: string) => sendPasswordResetEmail(firebaseAuth, email),
  verifyPasswordResetCode: (code: string) => verifyPasswordResetCode(firebaseAuth, code),
  confirmPasswordReset: (code: string, newPassword: string) =>
    confirmPasswordReset(firebaseAuth, code, newPassword),
  onAuthStateChanged: (nextOrObserver: NextOrObserver<User>) =>
    onAuthStateChanged(firebaseAuth, nextOrObserver),
  signOut: () => signOut(firebaseAuth),
} as const

export const storage = {
  uploadGiftImage: (currentUserId: User["uid"], image: File) =>
    Promise.resolve(ref(getStorage(app), `/admins/${currentUserId}/gift-images/${createId()}`))
      .then((ref) => uploadBytes(ref, image))
      .then((snapshot) => getDownloadURL(snapshot.ref)),
  uploadShopLogoImage: (currentUserId: User["uid"], image: File) =>
    Promise.resolve(ref(getStorage(app), `/admins/${currentUserId}/shop-logo-images/${createId()}`))
      .then((ref) => uploadBytes(ref, image))
      .then((snapshot) => getDownloadURL(snapshot.ref)),
  uploadTicketImage: (
    params: { restaurantId: string; shopId: string; ticketId: string },
    image: File
  ) =>
    Promise.resolve(
      ref(
        getStorage(app),
        `/restaurants/${params.restaurantId}/shops/${params.shopId}/tickets/${params.ticketId}/images/${createId()}-${image.name}`
      )
    )
      .then((ref) => uploadBytes(ref, image))
      .then((snapshot) => getDownloadURL(snapshot.ref)),
  copyGiftImage: (currentUserId: User["uid"], httpsUrl: string) =>
    Promise.resolve(ref(getStorage(app), httpsUrl))
      .then((ref) => getBlob(ref))
      .then((blob) =>
        uploadBytes(
          ref(getStorage(app), `/admins/${currentUserId}/gift-images/${createId()}`),
          blob
        )
      )
      .then((snapshot) => getDownloadURL(snapshot.ref)),
  listGiftCardTemplateImages: () =>
    listAll(ref(getStorage(app), "gift-card-template-images")).then((res) =>
      Promise.all(
        (res.items ?? []).map(async (i) => ({
          filename: i.name,
          url: await getDownloadURL(i),
        }))
      )
    ),
  listUploadedGiftCardTemplateImages: (currentUserId: User["uid"]) =>
    listAll(ref(getStorage(app), `/admins/${currentUserId}/gift-card-template-images`)).then(
      (res) =>
        Promise.all(
          (res.items ?? []).map(async (i) => ({
            filename: i.name,
            url: await getDownloadURL(i),
          }))
        )
    ),
  uploadGiftCardTemplateImages: (currentUserId: User["uid"], image: File) =>
    Promise.resolve(
      ref(
        getStorage(app),
        `/admins/${currentUserId}/gift-card-template-images/${createId()}-${image.name}`
      )
    )
      .then((ref) => uploadBytes(ref, image))
      // return filename
      .then((snapshot) => snapshot.metadata.name),
}
